import axios from "axios";

const TranslationService = {
  // 번역 캐시 (메모리 기반)
  translationCache: new Map(),

  // 캐시 만료 시간 (24시간)
  cacheExpiryHours: 24,

  // 캐시 키 생성
  generateCacheKey: (text, targetLanguage, sourceLanguage) => {
    return `${sourceLanguage}_${targetLanguage}_${text}`;
  },

  // 캐시에서 번역 결과 찾기
  getFromCache: (cacheKey) => {
    const cached = TranslationService.translationCache.get(cacheKey);
    if (cached) {
      const now = new Date().getTime();
      const hoursDiff = (now - cached.timestamp) / (1000 * 60 * 60);

      // 캐시가 유효한 경우
      if (hoursDiff < TranslationService.cacheExpiryHours) {
        return cached.translatedText;
      } else {
        // 만료된 캐시 삭제
        TranslationService.translationCache.delete(cacheKey);
      }
    }
    return null;
  },

  // 캐시에 번역 결과 저장
  saveToCache: (cacheKey, translatedText) => {
    TranslationService.translationCache.set(cacheKey, {
      translatedText,
      timestamp: new Date().getTime(),
    });
  },

  // 실시간 번역 함수
  translateText: async (text, targetLanguage, sourceLanguage = "ko") => {
    try {
      // 빈 텍스트나 null 체크
      if (!text || text.trim() === "") {
        return text;
      }

      // 소스 언어와 타겟 언어가 같으면 번역하지 않음
      if (sourceLanguage === targetLanguage) {
        return text;
      }

      // 캐시 키 생성
      const cacheKey = TranslationService.generateCacheKey(text, targetLanguage, sourceLanguage);

      // 캐시에서 먼저 확인
      const cachedResult = TranslationService.getFromCache(cacheKey);
      if (cachedResult) {
        return cachedResult;
      }

      const apiKey = process.env.REACT_APP_CLOUD_TRANSLATION_API_KEY;
      if (!apiKey) {
        console.error("Google Cloud Translation API key not found");
        return text;
      }

      const response = await axios.post(
        `https://translation.googleapis.com/language/translate/v2?key=${apiKey}`,
        {
          q: text,
          target: targetLanguage,
          source: sourceLanguage,
          format: "text",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data && response.data.data && response.data.data.translations) {
        const translatedText = response.data.data.translations[0].translatedText;

        // 결과를 캐시에 저장
        TranslationService.saveToCache(cacheKey, translatedText);

        return translatedText;
      } else {
        console.error("Invalid response from Google Translate API");
        return text;
      }
    } catch (error) {
      console.error("Translation error:", error);
      return text;
    }
  },

  // 캐시 통계 확인
  getCacheStats: () => {
    return {
      size: TranslationService.translationCache.size,
      keys: Array.from(TranslationService.translationCache.keys()),
    };
  },

  // 캐시 정리 (만료된 항목들 삭제)
  clearExpiredCache: () => {
    const now = new Date().getTime();
    let clearedCount = 0;

    for (const [key, value] of TranslationService.translationCache.entries()) {
      const hoursDiff = (now - value.timestamp) / (1000 * 60 * 60);
      if (hoursDiff >= TranslationService.cacheExpiryHours) {
        TranslationService.translationCache.delete(key);
        clearedCount++;
      }
    }

    console.log(`Cleared ${clearedCount} expired cache items`);
    return clearedCount;
  },

  // 전체 캐시 삭제
  clearAllCache: () => {
    const size = TranslationService.translationCache.size;
    TranslationService.translationCache.clear();
    console.log(`Cleared all ${size} cache items`);
    return size;
  },
};

export default TranslationService;
